import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc, 
  deleteDoc, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  addDoc,
  writeBatch,
  arrayUnion,
  arrayRemove
} from '../lib/offlineFirestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from '../lib/offlineStorage';
import { db, auth, storage } from '../config/firebase';
import { COLLECTIONS } from '../constants/collections';
import {
  CMS_PERMISSIONS,
  CMS_PERMISSION_GROUPS
} from '../constants/cmsAccess';
import { normalizeOperationalSettings } from '../config/operationalSettings';
import { normalizeTaxSettings } from '../config/taxSettings';
import { normalizeAppearanceSettings } from '../config/appearanceConfig';
import { normalizeDashboardSettings } from '../config/dashboardConfig';
import { normalizeReceiptDesignSettings, cacheReceiptDesignSettings } from '../config/receiptDesignerConfig';

const extractId = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const clean = value.trim();
    if (!clean) return '';
    return clean.includes('/')
      ? clean.split('/').filter(Boolean).pop() || ''
      : clean;
  }

  if (value.id) return String(value.id);

  if (value.path) {
    return String(value.path)
      .split('/')
      .filter(Boolean)
      .pop() || '';
  }

  return '';
};


const resolveTenantIdFromUserRecord = (data = {}) => (
  extractId(data?.tenantId) ||
  extractId(data?.tenantIdValue) ||
  extractId(data?.tenant) ||
  extractId(data?.companyId) ||
  extractId(data?.companyIdValue) ||
  extractId(data?.company) ||
  ''
);


const SETTINGS_CACHE_TTL_MS = 60 * 1000;
const companySettingsCache = new Map();
const companySettingsInFlight = new Map();

const readTimedCache = (cache, key) => {
  const entry = cache.get(String(key || ''));
  if (!entry) return null;
  if (Date.now() - entry.at > SETTINGS_CACHE_TTL_MS) {
    cache.delete(String(key || ''));
    return null;
  }
  return entry.value;
};

const resolveRoleRef = (roleId) => {
  if (!roleId) return null;

  // Normal Firestore DocumentReference written by assignRoleToUser().
  if (
    typeof roleId === 'object' &&
    roleId.path &&
    roleId.id
  ) {
    return roleId;
  }

  // Legacy/string role ids are also supported so role-based navigation
  // does not silently fail for older user documents.
  const id = extractId(roleId);
  return id ? doc(db, COLLECTIONS.ROLES, id) : null;
};


const DEFAULT_SYSTEM_ROLE_PERMISSION_SEEDS = Object.freeze({
  branch_manager: [
    'dashboard_view',
    'branches_view', 'branches_manage', 'branches_modules_manage', 'branches_switch', 'branches_reports', 'branches_transfers',
    'workflow_config_view', 'workflow_config_edit', 'workflow_combined_execute', 'workflow_stage_override', 'queue_override',
    'service_providers_view', 'service_providers_manage', 'service_provider_bills_manage',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share', 'internal_documents_manage',
    'addon_requests_view', 'addon_requests_create', 'custom_module_requests_create', 'addon_quotations_view',
    'agreements_view', 'agreements_create', 'agreements_edit',
    'outward_view', 'outward_create', 'outward_edit', 'outward_operations',
    'financial_year_view', 'financial_year_edit',
    'temperature_view',
    'temperature_alerts',
    'temperature_acknowledge',
    'maintenance_view',
    'services_view', 'deployment_view',
    'reports_export',
    'reports_management', 'reports_inward', 'reports_gate', 'reports_quality',
    'reports_lots', 'reports_storage', 'reports_inventory', 'reports_outward',
    'reports_growers', 'reports_hr'
  ],

  warehouse_manager: [
    'dashboard_view',
    'workflow_config_view', 'workflow_combined_execute', 'queue_override',
    'service_providers_view', 'service_providers_manage', 'service_provider_bills_manage',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'warehouse_create', 'chamber_operations', 'bin_operations', 'crate_operations',
    'outward_view', 'outward_create', 'outward_edit', 'outward_operations',
    'attendance_view',
    'temperature_view', 'temperature_edit',
    'temperature_configure', 'temperature_device_manage',
    'temperature_alerts', 'temperature_acknowledge',
    'temperature_export',
    'maintenance_view', 'maintenance_edit',
    'reports_export',
    'reports_management', 'reports_inward', 'reports_gate', 'reports_quality',
    'reports_lots', 'reports_storage', 'reports_inventory', 'reports_outward'
  ],

  operations_manager: [
    'dashboard_view',
    'workflow_config_view', 'workflow_config_edit', 'workflow_combined_execute', 'workflow_stage_override',
    'service_providers_view', 'service_providers_manage', 'service_provider_bills_manage',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'warehouse_create', 'chamber_operations', 'bin_operations', 'crate_operations',
    'outward_view', 'outward_create', 'outward_edit', 'outward_operations',
    'temperature_view',
    'temperature_alerts', 'temperature_acknowledge',
    'temperature_export',
    'maintenance_view',
    'reports_management', 'reports_inward', 'reports_gate', 'reports_quality',
    'reports_lots', 'reports_storage', 'reports_inventory', 'reports_outward'
  ],

  inventory_manager: [
    'dashboard_view',
    'bin_operations', 'crate_operations',
    'temperature_view',
    'reports_export',
    'reports_management', 'reports_inward', 'reports_lots',
    'reports_storage', 'reports_inventory'
  ],

  dispatch_manager: [
    'dashboard_view',
    'outward_view', 'outward_create', 'outward_edit', 'outward_operations',
    'reports_export',
    'reports_management', 'reports_gate', 'reports_inventory', 'reports_outward'
  ],

  maintenance_manager: [
    'dashboard_view', 'maintenance_view', 'maintenance_edit',
    'service_providers_view', 'service_providers_manage', 'service_provider_bills_manage',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'reports_view', 'reports_finance'
  ],

  document_controller: [
    'dashboard_view', 'internal_documents_view', 'internal_documents_upload',
    'internal_documents_share', 'internal_documents_manage', 'notifications_view'
  ],

  crm_executive: [
    'dashboard_view', 'customers_view', 'agreements_view',
    'crm_view', 'crm_manage', 'notifications_view', 'reports_view', 'reports_growers'
  ],

  gate_operator: [
    'dashboard_view',
    'gate_operations', 'security_operations',
    'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
    'reports_view', 'reports_gate'
  ],

  weighbridge_operator: [
    'dashboard_view',
    'weighbridge_operations',
    'weighment_view', 'weighment_create', 'weighment_verify',
    'reports_view', 'reports_gate', 'reports_inward'
  ],

  inward_operator: [
    'dashboard_view',
    'workflow_config_view', 'workflow_combined_execute',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'inward_operations',
    'inward_booking_view', 'inward_booking_create', 'inward_booking_edit',
    'reports_view', 'reports_inward', 'reports_gate',
    'reports_quality', 'reports_lots', 'reports_storage'
  ],

  combined_operations_operator: [
    'dashboard_view',
    'products_view', 'customers_view', 'agreements_view',
    'inward_operations', 'inward_booking_view', 'inward_booking_create', 'inward_booking_edit',
    'gate_operations', 'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
    'weighbridge_operations', 'weighment_view', 'weighment_create', 'weighment_verify',
    'unloading_view', 'unloading_create', 'unloading_verify',
    'quality_operations', 'inspection_view', 'inspection_create', 'inspection_approve',
    'lot_view', 'lot_create', 'lot_update',
    'storage_view', 'storage_create', 'storage_update', 'storage_allocate',
    'workflow_config_view', 'workflow_combined_execute',
    'notifications_view',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'reports_view', 'reports_inward', 'reports_gate', 'reports_quality', 'reports_lots', 'reports_storage'
  ],

  quality_inspector: [
    'dashboard_view',
    'unloading_view', 'unloading_create', 'unloading_verify',
    'quality_operations', 'inspection_view', 'inspection_create', 'inspection_approve', 'inspection_reject',
    'reports_quality', 'reports_inward'
  ],

  grading_supervisor: [
    'dashboard_view',
    'reports_quality', 'reports_lots'
  ],

  bin_controller: [
    'dashboard_view',
    'lot_view', 'lot_create', 'lot_update',
    'storage_view', 'storage_create', 'storage_update', 'storage_allocate',
    'reports_view', 'reports_storage', 'reports_inventory', 'reports_lots'
  ],

  chamber_manager: [
    'dashboard_view',
    'temperature_view', 'temperature_edit',
    'temperature_configure', 'temperature_device_manage',
    'temperature_alerts', 'temperature_acknowledge',
    'temperature_export',
    'reports_storage', 'reports_inventory'
  ],

  outward_operator: [
    'dashboard_view',
    'outward_view', 'outward_create', 'outward_edit',
    'reports_view', 'reports_outward', 'reports_inventory'
  ],

  security_guard: [
    'dashboard_view',
    'gate_operations', 'security_operations',
    'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
    'reports_view', 'reports_gate'
  ],

  finance_manager: [
    'dashboard_view',
    'service_providers_view', 'service_provider_bills_manage', 'service_provider_payments_manage',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'cash_operations',
    'reports_export', 'reports_finance', 'gst_reports_view', 'gst_reports_export'
  ],

  accountant: [
    'dashboard_view',
    'service_providers_view', 'service_provider_bills_manage', 'service_provider_payments_manage',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
    'cash_operations',
    'reports_export', 'reports_finance', 'gst_reports_view', 'gst_reports_export'
  ],

  cashier: [
    'dashboard_view'
  ],

  billing_executive: [
    'dashboard_view',
    'reports_finance'
  ],

  auditor: [
    'dashboard_view',
    'reports_export', 'reports_finance', 'gst_reports_view', 'gst_reports_export'
  ],

  hr_manager: [
    'dashboard_view',
    'internal_documents_view', 'internal_documents_upload', 'internal_documents_share', 'internal_documents_manage',
    'attendance_view', 'attendance_create', 'attendance_edit', 'attendance_settings',
    'attendance_operations', 'leave_operations', 'payroll_operations',
    'reports_export', 'reports_hr'
  ],

  hr_executive: [
    'dashboard_view',
    'attendance_view', 'attendance_create', 'attendance_edit',
    'reports_view', 'reports_hr'
  ],

  payroll_officer: [
    'dashboard_view',
    'reports_hr'
  ],

  trading_manager: [
    'dashboard_view',
    'reports_export', 'reports_trading'
  ],

  purchase_executive: [
    'dashboard_view',
    'reports_trading'
  ],

  sales_executive: [
    'dashboard_view',
    'reports_trading'
  ],

  company_owner: ['all'],
  company_admin: ['all'],

  // Portal roles stay portal-only. No CMS/sidebar reporting permission is added.
  customer: [],
  grower: [],
  sub_grower: [],
  transporter: []
});

const mergePermissions = (...permissionLists) => {
  const set = new Set(
    permissionLists
      .flat()
      .filter(
        (permission) =>
          typeof permission === 'string' &&
          permission.trim()
      )
      .map((permission) =>
        permission.trim().toLowerCase()
      )
  );

  const hasAny = (...keys) =>
    keys.some((key) => set.has(key));

  // Backward-compatible Agreement permissions for roles created before
  // agreements_* was split out from Grower/Customer management.
  if (hasAny('customers_view', 'growers_view')) {
    set.add('agreements_view');
  }

  if (hasAny('customers_create', 'growers_create')) {
    set.add('agreements_create');
  }

  if (hasAny('customers_edit', 'growers_edit')) {
    set.add('agreements_edit');
  }

  // Older singular keys are normalized too.
  if (set.has('agreement_view')) set.add('agreements_view');
  if (set.has('agreement_create')) set.add('agreements_create');
  if (set.has('agreement_edit')) set.add('agreements_edit');
  if (set.has('agreement_delete')) set.add('agreements_delete');

  return Array.from(set);
};

const roleKeyOf = (role = {}) => {
  if (role.key) return String(role.key);
  if (role.systemRoleKey) return String(role.systemRoleKey);

  const normalizedName = String(role.name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const aliases = {
    company_owner: 'company_owner',
    company_admin: 'company_admin',
    branch_manager: 'branch_manager',
    warehouse_manager: 'warehouse_manager',
    operations_manager: 'operations_manager',
    inventory_manager: 'inventory_manager',
    dispatch_manager: 'dispatch_manager',
    maintenance_manager: 'maintenance_manager',
    document_controller: 'document_controller',
    crm_executive: 'crm_executive',
    gate_operator: 'gate_operator',
    weighbridge_operator: 'weighbridge_operator',
    inward_operator: 'inward_operator',
    combined_operations_operator: 'combined_operations_operator',
    quality_inspector: 'quality_inspector',
    grading_supervisor: 'grading_supervisor',
    bin_controller: 'bin_controller',
    chamber_manager: 'chamber_manager',
    outward_operator: 'outward_operator',
    security_guard: 'security_guard',
    finance_manager: 'finance_manager',
    accountant: 'accountant',
    cashier: 'cashier',
    billing_executive: 'billing_executive',
    auditor: 'auditor',
    hr_manager: 'hr_manager',
    hr_executive: 'hr_executive',
    payroll_officer: 'payroll_officer',
    trading_manager: 'trading_manager',
    purchase_executive: 'purchase_executive',
    sales_executive: 'sales_executive',
    customer: 'customer',
    grower: 'grower',
    sub_grower: 'sub_grower',
    transporter: 'transporter'
  };

  return aliases[normalizedName] || normalizedName;
};

class SettingsService {
  
  // ============ COMPANY SETTINGS ============
  
  async getCompanySettings(tenantId, { force = false } = {}) {
    try {
      tenantId = extractId(tenantId);
      if (!tenantId) {
        const user = auth.currentUser;
        if (!user) throw new Error('User not authenticated');

        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        if (!userDoc.exists()) throw new Error('User not found');

        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
        if (!tenantId) throw new Error('No tenant associated with user');
      }

      tenantId = extractId(tenantId);
      if (!tenantId) throw new Error('No tenant associated with user');
      const key = String(tenantId);
      // Reuse an active request even when a caller asked to bypass the cache.
      // A forced refresh means "do not serve stale cached data", not "start a
      // duplicate Firestore listen/read for the same tenant".
      if (companySettingsInFlight.has(key)) return companySettingsInFlight.get(key);
      if (!force) {
        const cached = readTimedCache(companySettingsCache, key);
        if (cached) return cached;
      }

      const request = (async () => {
        const docRef = doc(db, COLLECTIONS.TENANTS, key);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) throw new Error('Company not found');
        const value = { id: docSnap.id, ...docSnap.data() };
        companySettingsCache.set(key, { at: Date.now(), value });
        cacheReceiptDesignSettings(key, value.receiptDesignSettings || value.settings?.receiptDesignSettings || {});
        return value;
      })();

      companySettingsInFlight.set(key, request);
      try {
        return await request;
      } finally {
        companySettingsInFlight.delete(key);
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
      throw error;
    }
  }

  peekCompanySettings(tenantId) {
    return readTimedCache(companySettingsCache, extractId(tenantId));
  }

  clearCompanySettingsCache(tenantId) {
    if (tenantId) companySettingsCache.delete(extractId(tenantId));
    else companySettingsCache.clear();
  }

  async updateCompanySettings(tenantId, data) {
    try {
      if (!tenantId) {
        const user = auth.currentUser;
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
      }

      tenantId = extractId(tenantId);
      if (!tenantId) throw new Error('No tenant associated with user');
      const docRef = doc(db, COLLECTIONS.TENANTS, tenantId);
      
      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      };

      // Merge-write keeps normal updates identical while also allowing a
      // legacy tenant parent document to be repaired when it is missing and
      // the signed-in account is permitted to create it.
      await setDoc(docRef, updateData, { merge: true });
      this.clearCompanySettingsCache(tenantId);
      
      return {
        success: true,
        message: 'Company settings updated successfully',
        id: tenantId
      };
    } catch (error) {
      console.error('Error updating company settings:', error);
      throw error;
    }
  }

  async uploadCompanyLogo(tenantId, file) {
    try {
      if (!tenantId) {
        const user = auth.currentUser;
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      if (file.size > 2 * 1024 * 1024) {
        throw new Error('Logo must be less than 2MB');
      }

      const companyDoc = await getDoc(doc(db, COLLECTIONS.TENANTS, tenantId));
      const oldLogo = companyDoc.data()?.logo;

      if (oldLogo) {
        try {
          const oldLogoRef = ref(storage, oldLogo);
          await deleteObject(oldLogoRef);
        } catch (error) {
          console.log('No old logo to delete or already deleted');
        }
      }

      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `logos/${tenantId}/logo_${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      await setDoc(doc(db, COLLECTIONS.TENANTS, tenantId), {
        logo: downloadURL,
        updatedAt: serverTimestamp()
      }, { merge: true });

      return {
        success: true,
        logoUrl: downloadURL,
        message: 'Logo uploaded successfully'
      };
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  }

  async removeCompanyLogo(tenantId) {
    try {
      if (!tenantId) {
        const user = auth.currentUser;
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
      }

      const companyDoc = await getDoc(doc(db, COLLECTIONS.TENANTS, tenantId));
      const logoUrl = companyDoc.data()?.logo;

      if (logoUrl) {
        const logoRef = ref(storage, logoUrl);
        await deleteObject(logoRef);
      }

      await setDoc(doc(db, COLLECTIONS.TENANTS, tenantId), {
        logo: '',
        updatedAt: serverTimestamp()
      }, { merge: true });

      return {
        success: true,
        message: 'Logo removed successfully'
      };
    } catch (error) {
      console.error('Error removing logo:', error);
      throw error;
    }
  }

  // ============ ROLE MANAGEMENT ============

  async getRoles(tenantId, category = null) {
    try {
      if (!tenantId) {
        const user = auth.currentUser;
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
      }

      let rolesQuery = query(
        collection(db, COLLECTIONS.ROLES),
        where('tenantId', '==', doc(db, COLLECTIONS.TENANTS, tenantId)),
        orderBy('priority', 'asc')
      );

      const snapshot = await getDocs(rolesQuery);
      let roles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (category && category !== 'all') {
        roles = roles.filter(role => role.category === category);
      }

      return roles;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  async getRole(roleId) {
    try {
      const docRef = doc(db, COLLECTIONS.ROLES, roleId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Role not found');
      }
      
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }

  async createRole(tenantId, roleData) {
    try {
      if (!tenantId) {
        const user = auth.currentUser;
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
      }

      if (!roleData.name) {
        throw new Error('Role name is required');
      }

      const rolesSnapshot = await getDocs(
        query(
          collection(db, COLLECTIONS.ROLES),
          where('tenantId', '==', doc(db, COLLECTIONS.TENANTS, tenantId))
        )
      );

      const role = {
        tenantId: doc(db, COLLECTIONS.TENANTS, tenantId),
        name: roleData.name,
        description: roleData.description || '',
        category: roleData.category || 'warehouse',
        permissions: roleData.permissions || [],
        isDefault: roleData.isDefault || false,
        isSystem: roleData.isSystem || false,
        priority: rolesSnapshot.size + 1,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.ROLES), role);
      
      return {
        success: true,
        id: docRef.id,
        role: { id: docRef.id, ...role },
        message: 'Role created successfully'
      };
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  async updateRole(roleId, roleData) {
    try {
      const docRef = doc(db, COLLECTIONS.ROLES, roleId);
      
      const roleDoc = await getDoc(docRef);
      if (!roleDoc.exists()) {
        throw new Error('Role not found');
      }

      const existingRole = roleDoc.data();
      if (existingRole.isSystem) {
        throw new Error('System roles cannot be modified');
      }

      const updateData = {
        name: roleData.name || existingRole.name,
        description: roleData.description !== undefined ? roleData.description : existingRole.description,
        category: roleData.category || existingRole.category,
        permissions: roleData.permissions || existingRole.permissions,
        isDefault: roleData.isDefault !== undefined ? roleData.isDefault : existingRole.isDefault,
        status: roleData.status || existingRole.status,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      };

      await updateDoc(docRef, updateData);
      
      return {
        success: true,
        id: roleId,
        message: 'Role updated successfully'
      };
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  async deleteRole(roleId) {
    try {
      const docRef = doc(db, COLLECTIONS.ROLES, roleId);
      
      const roleDoc = await getDoc(docRef);
      if (!roleDoc.exists()) {
        throw new Error('Role not found');
      }

      const roleData = roleDoc.data();
      if (roleData.isSystem) {
        throw new Error('System roles cannot be deleted');
      }

      const usersSnapshot = await getDocs(
        query(
          collection(db, COLLECTIONS.USERS),
          where('roleId', '==', docRef)
        )
      );

      if (!usersSnapshot.empty) {
        throw new Error('Cannot delete role that is assigned to users');
      }

      await deleteDoc(docRef);
      
      return {
        success: true,
        message: 'Role deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  async syncSystemRolePermissions(
    tenantId,
    suppliedRoles = null
  ) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      /*
       * STRICT ROLE MODE
       * ----------------
       * Firestore role.permissions is the ONLY source of authorization.
       * This method intentionally does NOT add permissions based on role name,
       * category, isSystem or isDefault. It only normalizes the role key and
       * removes duplicate/invalid permission strings already saved on the role.
       */
      const roles =
        Array.isArray(suppliedRoles)
          ? suppliedRoles
          : await this.getRoles(tenantId);

      const batch = writeBatch(db);
      let updatedCount = 0;

      roles.forEach((role) => {
        if (!role?.id) return;

        const roleKey = roleKeyOf(role);
        const currentPermissions = Array.isArray(role.permissions)
          ? role.permissions
          : [];

        const normalizedPermissions = mergePermissions(
          currentPermissions
        );

        const keyChanged =
          Boolean(roleKey) &&
          String(role.key || '') !== roleKey;

        const permissionsChanged =
          normalizedPermissions.length !== currentPermissions.length ||
          normalizedPermissions.some(
            (permission, index) =>
              permission !== currentPermissions[index]
          );

        if (!keyChanged && !permissionsChanged) {
          return;
        }

        batch.update(
          doc(db, COLLECTIONS.ROLES, role.id),
          {
            ...(roleKey ? { key: roleKey } : {}),
            permissions: normalizedPermissions,
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser?.uid || null
          }
        );

        updatedCount += 1;
      });

      if (updatedCount > 0) {
        await batch.commit();
      }

      return {
        success: true,
        updatedCount,
        message:
          updatedCount > 0
            ? `${updatedCount} role(s) normalized`
            : 'Role permissions are already normalized'
      };
    } catch (error) {
      console.error(
        'Error normalizing role permissions:',
        error
      );
      throw error;
    }
  }

  async initializeDefaultRoles(tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const existingRoles = await this.getRoles(tenantId);

      if (existingRoles.length > 0) {
        await this.syncSystemRolePermissions(
          tenantId,
          existingRoles
        );

        return {
          success: true,
          message: 'Default roles already exist and stored permissions were normalized',
          roles: await this.getRoles(tenantId)
        };
      }

      const PREDEFINED_ROLES = {
        // Owner Roles
        company_owner: {
          name: 'Company Owner',
          description: 'Complete control over all company operations and settings',
          category: 'owner',
          permissions: ['all'],
          isSystem: true,
          isDefault: true,
          priority: 1
        },
        // Admin Roles
        company_admin: {
          name: 'Company Admin',
          description: 'Daily administration and management of company operations',
          category: 'admin',
          permissions: ['all'],
          isSystem: true,
          isDefault: true,
          priority: 2
        },
        branch_manager: {
          name: 'Branch Manager',
          description: 'Controls and manages a specific branch/location',
          category: 'admin',
          permissions: [
            'branches_view', 'branches_manage', 'branches_modules_manage', 'branches_switch', 'branches_reports', 'branches_transfers',
            'products_view', 'products_create', 'products_edit', 'products_delete',
            'inventory_view', 'inventory_create', 'inventory_edit', 'inventory_adjust',
            'customers_view', 'customers_create', 'customers_edit', 'customers_delete',
            'warehouse_view', 'warehouse_edit',
            'dispatch_view', 'dispatch_create', 'dispatch_edit',
            'employees_view', 'employees_edit',
            'reports_view', 'reports_generate',
            'settings_view', 'settings_edit',
            // Inward permissions
            'inward_booking_view', 'inward_booking_create', 'inward_booking_edit', 'inward_booking_delete',
            'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
            'weighment_view', 'weighment_create', 'weighment_verify',
            'unloading_view', 'unloading_create', 'unloading_verify',
            'inspection_view', 'inspection_create', 'inspection_approve', 'inspection_reject',
            'grading_view', 'grading_create', 'grading_update', 'grading_verify',
            'lot_view', 'lot_create', 'lot_update',
            'storage_view', 'storage_create', 'storage_update', 'storage_allocate',
            'billing_view', 'billing_create', 'billing_approve',
            'quality_reject', 'quality_hold', 'quality_release'
          ],
          isSystem: true,
          isDefault: true,
          priority: 3
        },
        // Warehouse Roles
        warehouse_manager: {
          name: 'Warehouse Manager',
          description: 'Complete oversight of warehouse operations and staff',
          category: 'warehouse',
          permissions: [
            'products_view', 'products_create', 'products_edit',
            'inventory_view', 'inventory_create', 'inventory_edit', 'inventory_adjust',
            'warehouse_view', 'warehouse_edit',
            'dispatch_view', 'dispatch_create', 'dispatch_edit',
            'employees_view', 'employees_edit',
            'reports_view', 'reports_generate',
            'inward_booking_view', 'inward_booking_create',
            'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
            'weighment_view', 'weighment_create', 'weighment_verify',
            'unloading_view', 'unloading_create', 'unloading_verify',
            'inspection_view', 'inspection_create', 'inspection_approve',
            'grading_view', 'grading_create', 'grading_verify',
            'lot_view', 'lot_create',
            'storage_view', 'storage_create', 'storage_allocate',
            'billing_view'
          ],
          isSystem: true,
          isDefault: true,
          priority: 4
        },
        operations_manager: {
          name: 'Operations Manager',
          description: 'Complete warehouse workflow management from inward to outward',
          category: 'warehouse',
          permissions: [
            'products_view', 'products_create',
            'inventory_view', 'inventory_create', 'inventory_edit', 'inventory_adjust',
            'warehouse_view', 'warehouse_edit',
            'dispatch_view', 'dispatch_create', 'dispatch_edit',
            'reports_view',
            'inward_booking_view', 'inward_booking_create',
            'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
            'weighment_view', 'weighment_create', 'weighment_verify',
            'unloading_view', 'unloading_create', 'unloading_verify',
            'inspection_view', 'inspection_create', 'inspection_approve', 'inspection_reject',
            'grading_view', 'grading_create', 'grading_verify',
            'lot_view', 'lot_create',
            'storage_view', 'storage_create', 'storage_allocate',
            'billing_view'
          ],
          isSystem: true,
          priority: 5
        },
        inventory_manager: {
          name: 'Inventory Manager',
          description: 'Oversees inventory levels, FIFO/FEFO and stock accuracy',
          category: 'warehouse',
          permissions: [
            'products_view', 'products_create', 'products_edit',
            'inventory_view', 'inventory_create', 'inventory_edit', 'inventory_adjust',
            'warehouse_view',
            'reports_view', 'reports_generate',
            'inward_booking_view',
            'gate_entry_view',
            'weighment_view',
            'unloading_view',
            'inspection_view',
            'grading_view',
            'lot_view', 'lot_create',
            'storage_view', 'storage_create', 'storage_allocate'
          ],
          isSystem: true,
          priority: 6
        },
        dispatch_manager: {
          name: 'Dispatch Manager',
          description: 'Manages dispatch planning and delivery operations',
          category: 'warehouse',
          permissions: [
            'products_view',
            'inventory_view', 'inventory_edit',
            'warehouse_view',
            'dispatch_view', 'dispatch_create', 'dispatch_edit',
            'reports_view', 'reports_generate',
            'gate_entry_view'
          ],
          isSystem: true,
          priority: 7
        },
        maintenance_manager: {
          name: 'Maintenance & Service Manager',
          description: 'Manages service providers, maintenance work orders and provider bills; provider payments require Finance permission',
          category: 'warehouse',
          permissions: ['dashboard_view','maintenance_view','maintenance_edit','service_providers_view','service_providers_manage','service_provider_bills_manage','internal_documents_view','internal_documents_upload','internal_documents_share','reports_view','reports_finance'],
          isSystem: true,
          priority: 8
        },
        document_controller: {
          name: 'Document Controller',
          description: 'Controls internal documents, versions, sharing and acknowledgements',
          category: 'admin',
          permissions: ['dashboard_view','internal_documents_view','internal_documents_upload','internal_documents_share','internal_documents_manage','notifications_view'],
          isSystem: true,
          priority: 9
        },
        crm_executive: {
          name: 'CRM Executive',
          description: 'Manages grower CRM and follow-ups without Finance administration',
          category: 'admin',
          permissions: ['dashboard_view','customers_view','agreements_view','crm_view','crm_manage','notifications_view','reports_view','reports_growers'],
          isSystem: true,
          priority: 9
        },
        gate_operator: {
          name: 'Gate Operator',
          description: 'Manages vehicle gate entry and security checks',
          category: 'warehouse',
          permissions: [
            'products_view',
            'inventory_view',
            'warehouse_view',
            'dispatch_view',
            'gate_operations',
            'gate_entry_view',
            'gate_entry_create',
            'gate_entry_verify',
            'inward_booking_view',
            'security_operations'
          ],
          isSystem: true,
          priority: 10
        },
        weighbridge_operator: {
          name: 'Weighbridge Operator',
          description: 'Operates weighbridge for gross and net weight recording',
          category: 'warehouse',
          permissions: [
            'products_view',
            'inventory_view',
            'warehouse_view',
            'weighbridge_operations',
            'weighment_view', 'weighment_create', 'weighment_verify',
            'gate_entry_view'
          ],
          isSystem: true,
          priority: 11
        },
        inward_operator: {
          name: 'Inward Operator',
          description: 'Manages goods receipt and inward entry process',
          category: 'warehouse',
          permissions: [
            'products_view',
            'inventory_view', 'inventory_create',
            'warehouse_view',
            'inward_operations',
            'inward_booking_view', 'inward_booking_create', 'inward_booking_edit',
            'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
            'weighment_view', 'weighment_create', 'weighment_verify',
            'unloading_view', 'unloading_create', 'unloading_verify',
            'inspection_view', 'inspection_create', 'inspection_approve',
            'grading_view', 'grading_create', 'grading_verify',
            'lot_view', 'lot_create',
            'storage_view', 'storage_create', 'storage_allocate',
            'billing_view'
          ],
          isSystem: true,
          priority: 12
        },
        combined_operations_operator: {
          name: 'Combined Operations Operator',
          description: 'Small-store operator who can execute configured combined inward stages while stage records, finance and stock remain separate',
          category: 'warehouse',
          permissions: [
            'dashboard_view', 'products_view', 'customers_view', 'agreements_view',
            'inward_operations', 'inward_booking_view', 'inward_booking_create', 'inward_booking_edit',
            'gate_operations', 'gate_entry_view', 'gate_entry_create', 'gate_entry_verify',
            'weighbridge_operations', 'weighment_view', 'weighment_create', 'weighment_verify',
            'unloading_view', 'unloading_create', 'unloading_verify',
            'quality_operations', 'inspection_view', 'inspection_create', 'inspection_approve',
            'lot_view', 'lot_create', 'lot_update',
            'storage_view', 'storage_create', 'storage_update', 'storage_allocate',
            'workflow_config_view', 'workflow_combined_execute',
            'notifications_view',
            'internal_documents_view', 'internal_documents_upload', 'internal_documents_share',
            'reports_view', 'reports_inward', 'reports_gate', 'reports_quality', 'reports_lots', 'reports_storage'
          ],
          isSystem: true,
          priority: 13
        },
        quality_inspector: {
          name: 'Quality Inspector',
          description: 'Performs quality checks and inspection of incoming goods',
          category: 'warehouse',
          permissions: [
            'products_view',
            'inventory_view',
            'quality_operations',
            'reports_view',
            'inspection_view', 'inspection_create', 'inspection_approve', 'inspection_reject',
            'grading_view', 'grading_create', 'grading_verify',
            'quality_reject', 'quality_hold', 'quality_release'
          ],
          isSystem: true,
          priority: 13
        },
        grading_supervisor: {
          name: 'Grading Supervisor',
          description: 'Supervises product grading and quality classification',
          category: 'warehouse',
          permissions: [
            'products_view', 'products_edit',
            'inventory_view', 'inventory_edit',
            'grading_operations',
            'reports_view',
            'grading_view', 'grading_create', 'grading_update', 'grading_verify',
            'inspection_view',
            'lot_view'
          ],
          isSystem: true,
          priority: 14
        },
        bin_controller: {
          name: 'Bin Controller',
          description: 'Manages bin allocation and inventory location tracking',
          category: 'warehouse',
          permissions: [
            'inventory_view', 'inventory_edit', 'inventory_adjust',
            'warehouse_view', 'warehouse_edit',
            'bin_operations',
            'storage_view', 'storage_create', 'storage_update', 'storage_allocate',
            'lot_view'
          ],
          isSystem: true,
          priority: 15
        },
        chamber_manager: {
          name: 'Chamber Manager',
          description: 'Manages chamber allocation and temperature zones',
          category: 'warehouse',
          permissions: [
            'inventory_view', 'inventory_edit',
            'warehouse_view', 'warehouse_edit',
            'chamber_operations',
            'reports_view',
            'storage_view', 'storage_create', 'storage_update', 'storage_allocate'
          ],
          isSystem: true,
          priority: 16
        },
        outward_operator: {
          name: 'Outward Operator',
          description: 'Processes dispatch and outward movements',
          category: 'warehouse',
          permissions: [
            'products_view',
            'inventory_view', 'inventory_edit',
            'warehouse_view',
            'dispatch_view', 'dispatch_create', 'dispatch_edit',
            'outward_operations',
            'gate_entry_view'
          ],
          isSystem: true,
          priority: 17
        },
        security_guard: {
          name: 'Security Guard',
          description: 'Monitors warehouse security and access control',
          category: 'warehouse',
          permissions: [
            'warehouse_view',
            'security_operations',
            'gate_operations',
            'gate_entry_view',
            'gate_entry_verify'
          ],
          isSystem: true,
          priority: 18
        },
        // Finance Roles
        finance_manager: {
          name: 'Finance Manager',
          description: 'Oversees all financial operations and reporting',
          category: 'finance',
          permissions: [
            'invoices_view', 'invoices_create', 'invoices_edit',
            'payments_view', 'payments_process',
            'reports_view', 'reports_generate', 'gst_reports_view', 'gst_reports_export',
            'finance_operations',
            'billing_view', 'billing_create', 'billing_approve'
          ],
          isSystem: true,
          priority: 8
        },
        accountant: {
          name: 'Accountant',
          description: 'Manages day-to-day accounting and bookkeeping',
          category: 'finance',
          permissions: [
            'invoices_view', 'invoices_create', 'invoices_edit',
            'payments_view', 'payments_process',
            'reports_view', 'reports_generate', 'gst_reports_view', 'gst_reports_export',
            'finance_operations',
            'billing_view'
          ],
          isSystem: true,
          priority: 20
        },
        cashier: {
          name: 'Cashier',
          description: 'Handles cash transactions and petty cash management',
          category: 'finance',
          permissions: [
            'payments_view', 'payments_process',
            'cash_operations',
            'billing_view'
          ],
          isSystem: true,
          priority: 21
        },
        billing_executive: {
          name: 'Billing Executive',
          description: 'Manages billing, invoicing and payment collection',
          category: 'finance',
          permissions: [
            'invoices_view', 'invoices_create', 'invoices_edit',
            'payments_view',
            'billing_operations',
            'reports_view',
            'billing_view', 'billing_create', 'billing_approve'
          ],
          isSystem: true,
          priority: 22
        },
        auditor: {
          name: 'Auditor',
          description: 'Performs internal audits and compliance checks',
          category: 'finance',
          permissions: [
            'invoices_view',
            'payments_view',
            'reports_view', 'reports_generate',
            'audit_operations',
            'billing_view'
          ],
          isSystem: true,
          priority: 23
        },
        // HR Roles
        hr_manager: {
          name: 'HR Manager',
          description: 'Manages human resources and employee relations',
          category: 'hr',
          permissions: [
            'employees_view', 'employees_edit',
            'hr_operations',
            'reports_view', 'reports_generate'
          ],
          isSystem: true,
          priority: 24
        },
        hr_executive: {
          name: 'HR Executive',
          description: 'Handles recruitment, attendance and employee records',
          category: 'hr',
          permissions: [
            'employees_view', 'employees_edit',
            'attendance_operations',
            'leave_operations'
          ],
          isSystem: true,
          priority: 25
        },
        payroll_officer: {
          name: 'Payroll Officer',
          description: 'Manages payroll processing and salary disbursement',
          category: 'hr',
          permissions: [
            'employees_view',
            'payroll_operations',
            'reports_view'
          ],
          isSystem: true,
          priority: 26
        },
        // Trading Roles
        trading_manager: {
          name: 'Trading Manager',
          description: 'Overseas all trading operations and P&L tracking',
          category: 'trading',
          permissions: [
            'products_view', 'products_create', 'products_edit',
            'customers_view', 'customers_create', 'customers_edit',
            'inventory_view',
            'purchase_operations', 'sales_operations',
            'reports_view', 'reports_generate'
          ],
          isSystem: true,
          priority: 9
        },
        purchase_executive: {
          name: 'Purchase Executive',
          description: 'Manages purchase orders and vendor relationships',
          category: 'trading',
          permissions: [
            'products_view', 'products_create',
            'inventory_view', 'inventory_create',
            'purchase_operations',
            'reports_view'
          ],
          isSystem: true,
          priority: 27
        },
        sales_executive: {
          name: 'Sales Executive',
          description: 'Manages sales orders and customer relationships',
          category: 'trading',
          permissions: [
            'products_view',
            'customers_view', 'customers_create', 'customers_edit',
            'sales_operations',
            'reports_view'
          ],
          isSystem: true,
          priority: 28
        },
        // Customer Portal Roles
        customer: {
          name: 'Customer',
          description: 'Customer portal access - view orders, inventory and billing',
          category: 'customer',
          permissions: [
            'customer_portal_access',
            'customer_inventory_view',
            'customer_orders_view',
            'customer_billing_view'
          ],
          isSystem: true,
          priority: 30
        },
        grower: {
          name: 'Grower',
          description: 'Grower portal - manage produce and agreements',
          category: 'customer',
          permissions: [
            'grower_portal_access',
            'grower_products_view',
            'grower_agreements_view',
            'grower_inventory_view'
          ],
          isSystem: true,
          priority: 31
        },
        sub_grower: {
          name: 'Sub Grower',
          description: 'Sub-grower portal for supply chain management',
          category: 'customer',
          permissions: [
            'sub_grower_portal_access',
            'sub_grower_products_view',
            'sub_grower_agreements_view'
          ],
          isSystem: true,
          priority: 32
        },
        transporter: {
          name: 'Transporter',
          description: 'Transporter portal - manage deliveries and dispatch',
          category: 'customer',
          permissions: [
            'transporter_portal_access',
            'transporter_dispatch_view',
            'transporter_delivery_view'
          ],
          isSystem: true,
          priority: 33
        }
      };

      /*
       * These permissions are part of the INITIAL definition of the built-in
       * system roles only. They are written once when a tenant has no roles.
       * They are NOT merged at login/read time and are NOT re-added by sync.
       * Custom roles always receive only the permissions selected by the admin.
       */
      Object.entries(PREDEFINED_ROLES).forEach(
        ([roleKey, role]) => {
          if (role.permissions?.includes('all')) return;

          role.permissions = mergePermissions(
            role.permissions || [],
            DEFAULT_SYSTEM_ROLE_PERMISSION_SEEDS[roleKey] || []
          );
        }
      );

      const batch = writeBatch(db);
      const tenantRef = doc(db, COLLECTIONS.TENANTS, tenantId);

      for (const [key, roleData] of Object.entries(PREDEFINED_ROLES)) {
        const roleRef = doc(collection(db, COLLECTIONS.ROLES));
        batch.set(roleRef, {
          ...roleData,
          tenantId: tenantRef,
          key: key,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      await batch.commit();

      const roles = await this.getRoles(tenantId);
      
      return {
        success: true,
        message: 'Default roles initialized successfully',
        roles: roles
      };
    } catch (error) {
      console.error('Error initializing default roles:', error);
      throw error;
    }
  }

  async assignRoleToUser(userId, roleId) {
    try {
      if (!userId || !roleId) {
        throw new Error('User ID and Role ID are required');
      }

      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const roleRef = doc(db, COLLECTIONS.ROLES, roleId);

      const [roleDoc, userDoc] = await Promise.all([
        getDoc(roleRef),
        getDoc(userRef)
      ]);

      if (!roleDoc.exists()) {
        throw new Error('Role not found');
      }

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const roleData = roleDoc.data() || {};
      const userData = userDoc.data() || {};

      if (
        String(roleData.status || 'active').toLowerCase() === 'inactive'
      ) {
        throw new Error('Inactive roles cannot be assigned');
      }

      const roleTenantId = extractId(roleData.tenantId);
      const userTenantId = extractId(
        userData.tenantId ||
        userData.tenantRef ||
        userData.companyId
      );

      if (
        roleTenantId &&
        userTenantId &&
        roleTenantId !== userTenantId
      ) {
        throw new Error(
          'Cannot assign a role from another company'
        );
      }

      const roleKey = roleKeyOf(roleData);

      await updateDoc(userRef, {
        roleId: roleRef,
        roleIdValue: roleId,
        roleName: roleData.name || '',
        roleKey: roleKey || '',
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      });

      return {
        success: true,
        message: 'Role assigned successfully',
        userId,
        roleId,
        roleName: roleData.name || '',
        permissions: Array.isArray(roleData.permissions)
          ? [...roleData.permissions]
          : []
      };
    } catch (error) {
      console.error('Error assigning role:', error);
      throw error;
    }
  }

  async removeRoleFromUser(userId) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      
      await updateDoc(userRef, {
        roleId: null,
        roleIdValue: '',
        roleName: '',
        roleKey: '',
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      });

      return {
        success: true,
        message: 'Role removed successfully',
        userId: userId
      };
    } catch (error) {
      console.error('Error removing role:', error);
      throw error;
    }
  }

  // ============ USER MANAGEMENT ============

  async getTenantUsers(tenantId) {
    try {
      if (!tenantId) {
        const user = auth.currentUser;
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
        tenantId = resolveTenantIdFromUserRecord(userDoc.data());
      }

      const usersQuery = query(
        collection(db, COLLECTIONS.USERS),
        where('tenantId', '==', doc(db, COLLECTIONS.TENANTS, tenantId))
      );

      const snapshot = await getDocs(usersQuery);
      const users = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const userData = docSnap.data();
        let roleData = null;
        
        if (userData.roleId) {
          try {
            const roleRef = resolveRoleRef(userData.roleId);
            const roleDoc = roleRef ? await getDoc(roleRef) : null;
            if (roleDoc?.exists()) {
              roleData = {
                id: roleDoc.id,
                ...roleDoc.data()
              };
            }
          } catch (error) {
            console.log('Error fetching role for user:', error);
          }
        }

        return {
          id: docSnap.id,
          ...userData,
          role: roleData
        };
      }));

      return users;
    } catch (error) {
      console.error('Error fetching tenant users:', error);
      throw error;
    }
  }

  async getUser(userId) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = docSnap.data();
      let roleData = null;
      
      if (userData.roleId) {
        try {
          const roleRef = resolveRoleRef(userData.roleId);
          const roleDoc = roleRef ? await getDoc(roleRef) : null;
          if (roleDoc?.exists()) {
            roleData = {
              id: roleDoc.id,
              ...roleDoc.data()
            };
          }
        } catch (error) {
          console.log('Error fetching role:', error);
        }
      }

      if (roleData) {
        const userTenantId = extractId(
          userData.tenantId ||
          userData.tenantRef ||
          userData.companyId
        );
        const roleTenantId = extractId(roleData.tenantId);

        if (
          userTenantId &&
          roleTenantId &&
          userTenantId !== roleTenantId
        ) {
          console.warn(
            'Ignoring role from another tenant for user:',
            userId
          );
          roleData = null;
        }
      }

      return {
        id: docSnap.id,
        ...userData,
        role: roleData
      };
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async updateUserProfile(userId, data) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      });

      return {
        success: true,
        message: 'User updated successfully',
        userId: userId
      };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  getPermissionCatalog() {
    return {
      permissions: [...CMS_PERMISSIONS],
      groups: CMS_PERMISSION_GROUPS.map(
        (group) => ({
          ...group,
          permissions: [...group.permissions]
        })
      )
    };
  }

  getEffectiveRolePermissions(role = {}) {
    const permissions =
      Array.isArray(role.permissions)
        ? mergePermissions(role.permissions)
        : [];

    /*
     * STRICT ROLE MODE:
     * Never infer permissions from role name/key/category.
     * What is saved in role.permissions is what the user receives.
     */
    if (permissions.includes('all')) {
      return Array.from(
        new Set(['all', ...CMS_PERMISSIONS])
      );
    }

    return permissions;
  }

  // ============ PERMISSION MANAGEMENT ============

  async hasPermission(userId, permissionCode) {
    try {
      const user = await this.getUser(userId);
      if (!user.role) return false;

      if (
        String(user.role.status || 'active').toLowerCase() === 'inactive'
      ) {
        return false;
      }

      const permissions = this.getEffectiveRolePermissions(user.role);
      return permissions.includes('all') || permissions.includes(permissionCode);
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  async getUserPermissions(userId) {
    try {
      const user = await this.getUser(userId);

      if (!user.role) {
        return [];
      }

      if (
        String(user.role.status || 'active').toLowerCase() === 'inactive'
      ) {
        return [];
      }

      const permissions = Array.isArray(user.role.permissions)
        ? mergePermissions(user.role.permissions)
        : [];

      if (permissions.includes('all')) {
        return Array.from(
          new Set(['all', ...CMS_PERMISSIONS])
        );
      }

      /* Firestore role.permissions is authoritative. */
      return permissions;
    } catch (error) {
      console.error('Error getting user permissions:', error);
      return [];
    }
  }

  // ============ SETTINGS MANAGEMENT ============

  async getTenantSettings(tenantId) {
    try {
      const company = await this.getCompanySettings(tenantId);
      
      return {
        general: {
          companyName: company.companyName,
          tradingName: company.tradingName,
          registrationNumber: company.registrationNumber,
          taxId: company.taxId,
          industry: company.industry,
          tenantType: company.tenantType,
          status: company.status,
          contactEmail: company.contactEmail,
          contactPhone: company.contactPhone,
          website: company.website,
          logo: company.branding?.logoUrl || company.logo || company.logoUrl || company.companyLogo || '',
          logoUrl: company.branding?.logoUrl || company.logoUrl || company.logo || company.companyLogo || '',
          loginLogoUrl: company.branding?.loginLogoUrl || '',
          loginBackgroundUrl: company.branding?.loginBackgroundUrl || '',
          faviconUrl: company.branding?.faviconUrl || '',
          splashLogoUrl: company.branding?.splashLogoUrl || '',
          splashBackgroundUrl: company.branding?.splashBackgroundUrl || '',
          address: company.address
        },
        preferences: {
          timezone: company.settings?.timezone || 'America/New_York',
          currency: company.settings?.currency || 'USD',
          dateFormat: company.settings?.dateFormat || 'MM/DD/YYYY',
          timeFormat: company.settings?.timeFormat || '12h',
          language: company.settings?.language || 'en'
        },
        branding: {
          ...(company.branding || {}),
          primaryColor: company.branding?.primaryColor || '#1a5fa8',
          secondaryColor: company.branding?.secondaryColor || '#00b8e6',
          accentColor: company.branding?.accentColor || '#ff9800'
        },
        operationalSettings: normalizeOperationalSettings(company.operationalSettings || company.settings?.operationalSettings || {}),
        receiptDesignSettings: normalizeReceiptDesignSettings(company.receiptDesignSettings || company.settings?.receiptDesignSettings || {}),
        taxSettings: normalizeTaxSettings(company.taxSettings || company.settings?.taxSettings || { gstin: company.taxId || '' }),
        weighingSettings: company.weighingSettings || company.settings?.weighingSettings || {},
        formSettings: company.formSettings || company.settings?.formSettings || {},
        mobileAppConfig: company.mobileAppConfig || {},
        qrReaderSettings: company.qrReaderSettings || {},
        notifications: {
          emailNotifications: company.preferences?.emailNotifications ?? true,
          smsNotifications: company.preferences?.smsNotifications ?? false,
          lowStockAlerts: company.preferences?.lowStockAlerts ?? true,
          temperatureAlerts: company.preferences?.temperatureAlerts ?? true
        },
        workflowSettings: company.workflowSettings || company.settings?.workflowSettings || {},
        agreementDefaults: company.agreementDefaults || company.settings?.agreementDefaults || {},
        appearanceSettings: normalizeAppearanceSettings(company.appearanceSettings || company.settings?.appearanceSettings || {}),
        dashboardSettings: normalizeDashboardSettings(company.dashboardSettings || company.settings?.dashboardSettings || {}),
        navigationSettings: company.navigationSettings || company.settings?.navigationSettings || {},
        growerIdPrefix: company.growerIdPrefix || company.settings?.growerIdPrefix || 'GRW'
      };
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      throw error;
    }
  }

  async updateTenantSettings(tenantId, settings) {
    try {
      const updates = {};
      
      if (settings.general) {
        Object.assign(updates, settings.general);
      }
      
      if (settings.preferences) {
        const company = await this.getCompanySettings(tenantId);
        updates.settings = { ...(company.settings || {}), ...settings.preferences };
      }
      
      if (settings.branding) {
        const { themePreset, ...branding } = settings.branding || {};
        updates.branding = branding;
      }

      if (settings.operationalSettings) {
        updates.operationalSettings = normalizeOperationalSettings(settings.operationalSettings);
      }

      if (settings.taxSettings) {
        updates.taxSettings = normalizeTaxSettings(settings.taxSettings);
      }

      if (settings.appearanceSettings) {
        updates.appearanceSettings = normalizeAppearanceSettings(settings.appearanceSettings);
      }

      if (settings.dashboardSettings) {
        updates.dashboardSettings = normalizeDashboardSettings(settings.dashboardSettings);
      }

      if (settings.navigationSettings) {
        updates.navigationSettings = settings.navigationSettings;
      }

      if (settings.weighingSettings) {
        updates.weighingSettings = settings.weighingSettings;
      }

      if (settings.formSettings) {
        updates.formSettings = settings.formSettings;
      }

      if (settings.mobileAppConfig) {
        updates.mobileAppConfig = settings.mobileAppConfig;
      }

      if (settings.qrReaderSettings) {
        updates.qrReaderSettings = settings.qrReaderSettings;
      }
      
      if (settings.notifications) {
        updates.preferences = settings.notifications;
      }

      if (settings.workflowSettings) {
        updates.workflowSettings = settings.workflowSettings;
      }

      if (settings.agreementDefaults) {
        updates.agreementDefaults = settings.agreementDefaults;
      }

      if (settings.growerIdPrefix !== undefined) {
        updates.growerIdPrefix = String(settings.growerIdPrefix || 'GRW').trim().toUpperCase();
      }

      return await this.updateCompanySettings(tenantId, updates);
    } catch (error) {
      console.error('Error updating tenant settings:', error);
      throw error;
    }
  }

  // ============ AUDIT LOGS ============

  async logAuditAction(data) {
    try {
      const auditData = {
        ...data,
        tenantId: data.tenantId ? doc(db, COLLECTIONS.TENANTS, data.tenantId) : null,
        userId: auth.currentUser?.uid || null,
        userEmail: auth.currentUser?.email || null,
        timestamp: serverTimestamp(),
        ipAddress: data.ipAddress || null,
        userAgent: data.userAgent || null
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.AUDIT_LOGS), auditData);
      
      return {
        success: true,
        id: docRef.id,
        message: 'Audit log created'
      };
    } catch (error) {
      console.error('Error logging audit action:', error);
      throw error;
    }
  }

  async getAuditLogs(tenantId, limit = 100) {
    try {
      const logsQuery = query(
        collection(db, COLLECTIONS.AUDIT_LOGS),
        where('tenantId', '==', doc(db, COLLECTIONS.TENANTS, tenantId)),
        orderBy('timestamp', 'desc'),
        limit(limit)
      );

      const snapshot = await getDocs(logsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  // ============ BRANCH MANAGEMENT ============

  async createBranch(tenantId, branchData = {}) {
    try {
      const tenantKey = extractId(tenantId);
      const name = String(branchData.name || '').trim();
      if (!tenantKey) throw new Error('Tenant ID is required');
      if (!name) throw new Error('Branch name is required');

      const code = String(branchData.code || name.substring(0, 4))
        .trim().toUpperCase().replace(/[^A-Z0-9_-]+/g, '-').slice(0, 20);
      const existing = await this.getBranches(tenantKey);
      if (existing.some((row) => String(row.code || '').toUpperCase() === code)) {
        throw new Error(`Branch code ${code} already exists`);
      }

      const branch = {
        tenantId: doc(db, COLLECTIONS.TENANTS, tenantKey),
        tenantIdValue: tenantKey,
        name,
        code,
        branchType: String(branchData.branchType || 'cold_storage'),
        address: branchData.address && typeof branchData.address === 'object' ? branchData.address : {},
        contactEmail: String(branchData.contactEmail || '').trim(),
        contactPhone: String(branchData.contactPhone || '').trim(),
        gstNumber: String(branchData.gstNumber || '').trim().toUpperCase(),
        manager: branchData.manager || null,
        managerName: String(branchData.managerName || '').trim(),
        moduleMode: branchData.moduleMode === 'custom' ? 'custom' : 'inherit',
        moduleKeys: Array.isArray(branchData.moduleKeys) ? Array.from(new Set(branchData.moduleKeys.map(String).filter(Boolean))) : [],
        financeMode: ['inherit','centralized','separate'].includes(String(branchData.financeMode || '')) ? String(branchData.financeMode) : 'inherit',
        isHeadOffice: branchData.isHeadOffice === true,
        allowConsolidatedReports: branchData.allowConsolidatedReports !== false,
        status: ['active','inactive','suspended'].includes(String(branchData.status || '').toLowerCase()) ? String(branchData.status).toLowerCase() : 'active',
        notes: String(branchData.notes || '').trim().slice(0, 1200),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.BRANCHES), branch);
      return { success: true, id: docRef.id, branch: { id: docRef.id, ...branch }, message: 'Branch created successfully' };
    } catch (error) {
      console.error('Error creating branch:', error);
      throw error;
    }
  }

  async getBranches(tenantId) {
    try {
      const tenantKey = extractId(tenantId);
      if (!tenantKey) return [];
      const refCollection = collection(db, COLLECTIONS.BRANCHES);
      const tenantReference = doc(db, COLLECTIONS.TENANTS, tenantKey);
      const attempts = await Promise.allSettled([
        getDocs(query(refCollection, where('tenantIdValue', '==', tenantKey))),
        getDocs(query(refCollection, where('tenantId', '==', tenantReference))),
        getDocs(query(refCollection, where('tenantId', '==', tenantKey))),
      ]);
      const merged = new Map();
      attempts.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.docs.forEach((snap) => merged.set(snap.id, { id: snap.id, ...snap.data() }));
      });
      return Array.from(merged.values()).sort((a, b) => {
        if (Boolean(a.isHeadOffice) !== Boolean(b.isHeadOffice)) return a.isHeadOffice ? -1 : 1;
        return String(a.name || a.code || a.id).localeCompare(String(b.name || b.code || b.id));
      });
    } catch (error) {
      console.error('Error fetching branches:', error);
      throw error;
    }
  }

  async updateBranch(branchId, data = {}) {
    try {
      const id = extractId(branchId);
      if (!id) throw new Error('Branch ID is required');
      const patch = { ...data };
      delete patch.id;
      delete patch.tenantId;
      delete patch.tenantIdValue;
      delete patch.createdAt;
      delete patch.createdBy;
      if (Object.prototype.hasOwnProperty.call(patch, 'name')) patch.name = String(patch.name || '').trim();
      if (Object.prototype.hasOwnProperty.call(patch, 'code')) patch.code = String(patch.code || '').trim().toUpperCase().replace(/[^A-Z0-9_-]+/g, '-').slice(0, 20);
      if (Object.prototype.hasOwnProperty.call(patch, 'contactEmail')) patch.contactEmail = String(patch.contactEmail || '').trim();
      if (Object.prototype.hasOwnProperty.call(patch, 'contactPhone')) patch.contactPhone = String(patch.contactPhone || '').trim();
      if (Object.prototype.hasOwnProperty.call(patch, 'gstNumber')) patch.gstNumber = String(patch.gstNumber || '').trim().toUpperCase();
      if (Object.prototype.hasOwnProperty.call(patch, 'moduleKeys')) patch.moduleKeys = Array.isArray(patch.moduleKeys) ? Array.from(new Set(patch.moduleKeys.map(String).filter(Boolean))) : [];
      if (Object.prototype.hasOwnProperty.call(patch, 'moduleMode')) patch.moduleMode = patch.moduleMode === 'custom' ? 'custom' : 'inherit';
      await updateDoc(doc(db, COLLECTIONS.BRANCHES, id), {
        ...patch,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      });
      return { success: true, message: 'Branch updated successfully' };
    } catch (error) {
      console.error('Error updating branch:', error);
      throw error;
    }
  }

  async deleteBranch(branchId) {
    try {
      const id = extractId(branchId);
      if (!id) throw new Error('Branch ID is required');
      const branchRef = doc(db, COLLECTIONS.BRANCHES, id);
      const attempts = await Promise.allSettled([
        getDocs(query(collection(db, COLLECTIONS.USERS), where('branchId', '==', branchRef))),
        getDocs(query(collection(db, COLLECTIONS.USERS), where('branchId', '==', id))),
        getDocs(query(collection(db, COLLECTIONS.EMPLOYEES), where('branchId', '==', branchRef))),
        getDocs(query(collection(db, COLLECTIONS.EMPLOYEES), where('branchId', '==', id))),
      ]);
      const assigned = attempts.some((result) => result.status === 'fulfilled' && !result.value.empty);
      if (assigned) throw new Error('Cannot delete a branch that has users or employees assigned. Disable it instead.');
      await deleteDoc(branchRef);
      return { success: true, message: 'Branch deleted successfully' };
    } catch (error) {
      console.error('Error deleting branch:', error);
      throw error;
    }
  }


  async createBranchTransfer(tenantId, data = {}) {
    try {
      const tenantKey = extractId(tenantId);
      const sourceBranchId = extractId(data.sourceBranchId);
      const destinationBranchId = extractId(data.destinationBranchId);
      if (!tenantKey || !sourceBranchId || !destinationBranchId) throw new Error('Source and destination branches are required');
      if (sourceBranchId === destinationBranchId) throw new Error('Source and destination branches must be different');
      const quantity = Number(data.quantity || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) throw new Error('Transfer quantity must be greater than zero');
      const ref = await addDoc(collection(db, COLLECTIONS.BRANCH_TRANSFERS || 'branchTransfers'), {
        tenantId: doc(db, COLLECTIONS.TENANTS, tenantKey),
        tenantIdValue: tenantKey,
        sourceBranchId,
        sourceBranchName: String(data.sourceBranchName || ''),
        destinationBranchId,
        destinationBranchName: String(data.destinationBranchName || ''),
        transferType: String(data.transferType || 'stock'),
        itemName: String(data.itemName || '').trim(),
        quantity,
        unit: String(data.unit || 'unit').trim(),
        referenceNumber: String(data.referenceNumber || '').trim(),
        notes: String(data.notes || '').trim().slice(0, 1200),
        status: 'requested',
        history: [{ status: 'requested', at: new Date().toISOString(), by: auth.currentUser?.uid || '' }],
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: ref.id };
    } catch (error) {
      console.error('Error creating branch transfer:', error);
      throw error;
    }
  }

  async getBranchTransfers(tenantId) {
    try {
      const tenantKey = extractId(tenantId);
      if (!tenantKey) return [];
      const collectionName = COLLECTIONS.BRANCH_TRANSFERS || 'branchTransfers';
      const refCollection = collection(db, collectionName);
      const tenantReference = doc(db, COLLECTIONS.TENANTS, tenantKey);
      const attempts = await Promise.allSettled([
        getDocs(query(refCollection, where('tenantIdValue', '==', tenantKey))),
        getDocs(query(refCollection, where('tenantId', '==', tenantReference))),
        getDocs(query(refCollection, where('tenantId', '==', tenantKey))),
      ]);
      const rows = new Map();
      attempts.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        result.value.docs.forEach((snap) => rows.set(snap.id, { id: snap.id, ...snap.data() }));
      });
      const timestamp = (value) => value?.toMillis?.() || value?.toDate?.()?.getTime?.() || new Date(value || 0).getTime() || 0;
      return Array.from(rows.values()).sort((a,b)=>timestamp(b.updatedAt || b.createdAt)-timestamp(a.updatedAt || a.createdAt));
    } catch (error) {
      console.error('Error fetching branch transfers:', error);
      throw error;
    }
  }

  async updateBranchTransferStatus(transferId, status, notes = '') {
    try {
      const id = extractId(transferId);
      const nextStatus = String(status || '').toLowerCase();
      const allowed = ['requested','approved','dispatched','in_transit','received','reconciled','cancelled'];
      if (!id || !allowed.includes(nextStatus)) throw new Error('A valid transfer and status are required');
      const ref = doc(db, COLLECTIONS.BRANCH_TRANSFERS || 'branchTransfers', id);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error('Branch transfer not found');
      const current = snap.data() || {};
      const history = Array.isArray(current.history) ? current.history : [];
      await updateDoc(ref, {
        status: nextStatus,
        statusNotes: String(notes || '').trim().slice(0, 600),
        history: [...history, { status: nextStatus, at: new Date().toISOString(), by: auth.currentUser?.uid || '' }],
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null,
      });
      return { success: true, status: nextStatus };
    } catch (error) {
      console.error('Error updating branch transfer:', error);
      throw error;
    }
  }

}

// Export singleton instance
const settingsService = new SettingsService();
export default settingsService;